import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Rocket, ArrowLeft, Trophy, Star, Volume2,
  RotateCcw, Sparkles, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const RhymeTimeRocketGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [lives, setLives] = useState(3);
  const [currentWord, setCurrentWord] = useState(null);
  const [options, setOptions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [rocketLaunching, setRocketLaunching] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Rhyme families
  const rhymeFamilies = {
    1: [
      { base: 'cat', rhymes: ['hat', 'bat', 'mat', 'rat', 'sat', 'fat'], nonRhymes: ['dog', 'cup', 'sun'] },
      { base: 'dog', rhymes: ['fog', 'log', 'jog', 'hog', 'frog'], nonRhymes: ['cat', 'pen', 'bed'] },
      { base: 'sun', rhymes: ['run', 'fun', 'bun', 'gun', 'nun'], nonRhymes: ['hat', 'dog', 'cup'] },
      { base: 'bed', rhymes: ['red', 'fed', 'led', 'wed', 'shed'], nonRhymes: ['sun', 'cat', 'pen'] },
      { base: 'pig', rhymes: ['big', 'dig', 'fig', 'wig', 'jig'], nonRhymes: ['dog', 'hat', 'bed'] },
    ],
    2: [
      { base: 'cake', rhymes: ['bake', 'lake', 'make', 'wake', 'snake'], nonRhymes: ['dog', 'fish', 'bird'] },
      { base: 'fish', rhymes: ['dish', 'wish', 'swish'], nonRhymes: ['cake', 'tree', 'moon'] },
      { base: 'tree', rhymes: ['bee', 'see', 'free', 'knee', 'three'], nonRhymes: ['fish', 'cake', 'star'] },
      { base: 'moon', rhymes: ['soon', 'spoon', 'tune', 'noon', 'balloon'], nonRhymes: ['tree', 'fish', 'cake'] },
      { base: 'star', rhymes: ['car', 'far', 'jar', 'bar'], nonRhymes: ['moon', 'bee', 'cake'] },
    ],
    3: [
      { base: 'night', rhymes: ['light', 'right', 'bright', 'fight', 'sight'], nonRhymes: ['chair', 'phone', 'queen'] },
      { base: 'chair', rhymes: ['hair', 'pair', 'fair', 'bear', 'stair'], nonRhymes: ['night', 'phone', 'train'] },
      { base: 'train', rhymes: ['rain', 'brain', 'chain', 'plain', 'Spain'], nonRhymes: ['chair', 'night', 'phone'] },
      { base: 'phone', rhymes: ['bone', 'stone', 'cone', 'zone', 'alone'], nonRhymes: ['train', 'chair', 'night'] },
      { base: 'sheep', rhymes: ['deep', 'sleep', 'keep', 'creep', 'leap'], nonRhymes: ['phone', 'train', 'night'] },
    ]
  };

  const generateQuestion = useCallback(() => {
    const families = rhymeFamilies[Math.min(level, 3)];
    const family = families[Math.floor(Math.random() * families.length)];
    
    // Pick correct rhyme
    const correctRhyme = family.rhymes[Math.floor(Math.random() * family.rhymes.length)];
    
    // Pick wrong options
    const wrongOptions = family.nonRhymes.slice(0, 3);
    
    // Shuffle options
    const allOptions = [correctRhyme, ...wrongOptions].sort(() => Math.random() - 0.5);
    
    setCurrentWord({ base: family.base, correctRhyme, family });
    setOptions(allOptions);
    setRocketLaunching(false);
  }, [level]);

  const playSound = async (text) => {
    if (isPlaying) return;
    setIsPlaying(true);
    
    try {
      const response = await api.post('/tts/generate', {
        text: text,
        voice: 'nova'
      });
      
      if (response.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audio.onended = () => setIsPlaying(false);
        audio.play();
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setLives(3);
    setLevel(1);
    setCorrectAnswers(0);
    generateQuestion();
  }, [generateQuestion]);

  const handleAnswer = (selectedWord) => {
    if (currentWord.family.rhymes.includes(selectedWord)) {
      // Correct rhyme!
      setRocketLaunching(true);
      const points = 20 + (level * 10);
      setScore(s => s + points);
      setCorrectAnswers(c => c + 1);
      
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-2xl">🚀</span>
          <span>+{points} points! {currentWord.base} and {selectedWord} rhyme!</span>
        </div>
      );
      
      // Level up every 5 correct
      if ((correctAnswers + 1) % 5 === 0 && level < 3) {
        setLevel(l => l + 1);
        toast.success(`Level ${level + 1}! Harder rhymes ahead! 🌟`);
      }
      
      setTimeout(() => generateQuestion(), 1000);
    } else {
      // Wrong
      setLives(l => {
        const newLives = l - 1;
        if (newLives <= 0) {
          setGameState('finished');
        }
        return newLives;
      });
      toast.error(`Oops! ${currentWord.base} and ${selectedWord} don't rhyme.`);
    }
  };

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'rhyme-time-rocket',
            score: score,
            time_seconds: 0,
            metadata: { level, correct_answers: correctAnswers }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, correctAnswers, api]);

  return (
    <StudentLayout>
      <div data-testid="rhyme-time-rocket-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <Heart key={i} className={`w-6 h-6 ${i < lives ? 'text-red-500 fill-red-500' : 'text-slate-300'}`} />
                ))}
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-3xl">
              🚀
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Rhyme Time Rocket</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Level ${level} • Find the rhyming word!` : 'Find words that rhyme!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-indigo-900 to-purple-900 relative overflow-hidden">
            {/* Stars */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
            
            <div className="relative z-10">
              <div className="text-8xl mb-6 animate-bounce">🚀</div>
              <h2 className="font-heading text-3xl font-bold text-white mb-4">Rhyme Time Rocket</h2>
              <p className="text-lg text-purple-200 mb-6">
                Launch your rocket by finding words that rhyme!
              </p>
              <div className="text-purple-200 mb-8 space-y-2">
                <p>🔊 Listen to the word</p>
                <p>🎯 Find a word that sounds the same at the end</p>
                <p>🚀 Correct = Launch the rocket!</p>
              </div>
              <Button onClick={startGame} className="bg-purple-500 hover:bg-purple-600 text-white text-lg px-10 py-6">
                Blast Off!
                <Rocket className="w-6 h-6 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentWord && (
          <div className="student-card bg-gradient-to-b from-indigo-900 to-purple-900 min-h-[450px] relative overflow-hidden">
            {/* Stars background */}
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
            
            <div className="relative z-10">
              {/* Rocket */}
              <div className="text-center mb-6">
                <div className={`text-8xl transition-all duration-1000 ${rocketLaunching ? '-translate-y-32 opacity-0' : ''}`}>
                  🚀
                </div>
              </div>
              
              {/* Main Word */}
              <div className="text-center mb-8">
                <p className="text-purple-300 text-lg mb-2">Find a word that rhymes with:</p>
                <div className="bg-white/10 rounded-2xl p-6 inline-block">
                  <button
                    onClick={() => playSound(currentWord.base)}
                    className="flex items-center gap-3 hover:scale-105 transition-all"
                  >
                    <Volume2 className={`w-8 h-8 text-purple-300 ${isPlaying ? 'animate-pulse' : ''}`} />
                    <span className="text-5xl font-heading font-bold text-white">{currentWord.base}</span>
                  </button>
                </div>
              </div>

              {/* Hint */}
              <div className="text-center mb-6">
                <p className="text-purple-400 text-sm">
                  Hint: {currentWord.base} ends with "-{currentWord.base.slice(-2)}"
                </p>
              </div>

              {/* Answer Options */}
              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                {options.map((word) => (
                  <button
                    key={word}
                    onClick={() => handleAnswer(word)}
                    className="bg-white/10 backdrop-blur rounded-2xl p-5 hover:bg-white/20 hover:scale-105 transition-all active:scale-95 border-2 border-purple-400/30"
                  >
                    <span className="text-2xl font-heading font-bold text-white">{word}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-indigo-900 to-purple-900 relative overflow-hidden">
            {/* Stars */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              />
            ))}
            
            <div className="relative z-10">
              <div className="w-24 h-24 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <Trophy className="w-14 h-14 text-white" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-white mb-2">Mission Complete!</h2>
              <p className="text-xl text-purple-200 mb-4">
                You found {correctAnswers} rhymes!
              </p>
              
              <div className="flex items-center justify-center gap-2 mb-8">
                <Star className="w-8 h-8 text-amber-300 fill-amber-300" />
                <span className="font-heading text-4xl font-bold text-white">+{score}</span>
                <span className="text-purple-200 text-xl">points</span>
              </div>
              
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={startGame} className="border-purple-400 text-purple-200 hover:bg-purple-800">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Play Again
                </Button>
                <Button onClick={() => navigate('/student/games')} className="bg-purple-500 hover:bg-purple-600">
                  More Games
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default RhymeTimeRocketGame;
